<script setup lang="ts">
import { computed, onMounted, ref, toRefs, watch } from "vue";
import Button from "@/stories/buttons/Button/Button.vue";
import SelectInput from "@/stories/form/SelectInput/SelectInput.vue";
import { DAYS_IN_WEEK } from "@/consts";
import { useI18n } from "vue-i18n";
import TextInput from "@/stories/form/TextInput/TextInput.vue";
import Checkbox from "@/stories/form/Checkbox/Checkbox.vue";
import { useBreakpoints } from "@vueuse/core";
import { BREAKPOINTS } from "@/consts";
import type {
  PrimitiveModel,
  RepeatRuleEndingTypes,
  RRuleState,
} from "@/types";
import getDefaultRRuleState from "@/helpers/rrules/getDefaultRRuleState";
import Line from "@/stories/layout/Line/Line.vue";
import joi from "joi";
import { validate } from "@/helpers/forms/validate";
import type { IMultiSelectItem, IValidationResult } from "@/interfaces";
import BigDialog from "@/stories/dialogs/BigDialog.vue";
import DialogHeader from "@/stories/dialogs/DialogHeader.vue";
import DialogContent from "@/stories/dialogs/DialogContent.vue";
import DialogFooterActions from "@/stories/dialogs/DialogFooterActions.vue";
import HorizontalForm from "@/stories/form/HorizontalForm/HorizontalForm.vue";
import { usePlannerTemplateRepeats } from "@/composables/usePlannerTemplateRepeats";
import TagSelectorInput from "@/stories/form/TagSelectorInput/TagSelectorInput.vue";
import FormGroup from "@/stories/form/FormGroup/FormGroup.vue";
import DatePicker from "@/stories/form/DatePicker/DatePicker.vue";
import { toUnix } from "@/stories/utils/dates";
import { addDays, addMonths } from "date-fns";
import { useRRuleTypeString } from "@/composables/useRRuleTypeString";
import RRuleTextFetcher from "@/stories/displays/RRuleText/RRuleTextFetcher.vue";
import useRepeatRuleLists from "@/composables/useRepeatRuleLists";
import isValidRRule from "@/helpers/rrules/isValidRRule";
import { getDefaultSelectedDaysForWeek } from "@/views/planner/normalizeNewTaskEntry";

interface Props {
  initialValue?: Partial<RRuleState>;
}

const props = defineProps<Props>();

const emit = defineEmits<{ close: []; update: [RRuleState] }>();

const { initialValue } = toRefs(props);

const { t } = useI18n();
const breakpoints = useBreakpoints(BREAKPOINTS);
const isMobile = breakpoints.smaller("tablet");

const { repeatRuleOptions } = usePlannerTemplateRepeats();
const { ORDER_LIST, DAYS_LIST } = useRepeatRuleLists();

const getNextEndingDate = () => toUnix(addDays(new Date(), 7));

const rruleState = ref<RRuleState>({
  ...getDefaultRRuleState(),
  endingType: "finish_on",
  endingDate: getNextEndingDate(),
  useBysetpos: true,
  days: getDefaultSelectedDaysForWeek(),
  ...initialValue.value,
});
const validation = ref<IValidationResult | null>();

onMounted(() => {
  setMonthlyValues(true);
});

const { typeString } = useRRuleTypeString(rruleState);

const VALIDATION_SCHEMA = joi.object({
  occurence: joi.number().required().min(1),
  endingDate: joi.number().required().min(1),
  repeatAmount: joi.number().required().min(1).max(9999),
});

const handleSave = () => {
  validation.value = validate(VALIDATION_SCHEMA, {
    ...rruleState.value,
  });

  if (validation.value.isValid && isValidRepeatRule.value) {
    emit("update", rruleState.value);
  }
};

/**
 * Sets the default monthly values and ending date. When `keepInitialValues`
 * is set, the values coming from the initial rule are kept, so that editing an
 * existing rule doesn't silently reset its ending date or monthly day.
 */
const setMonthlyValues = (keepInitialValues = false) => {
  const hasInitialEndingDate =
    keepInitialValues && !!initialValue.value?.endingDate;

  if (rruleState.value.cadence === 30) {
    rruleState.value.dayOrder = keepInitialValues
      ? (rruleState.value.dayOrder ?? 1)
      : 1;
    rruleState.value.daySelection = keepInitialValues
      ? (rruleState.value.daySelection ?? 0)
      : 0;

    if (!hasInitialEndingDate) {
      rruleState.value.endingDate = toUnix(addMonths(new Date(), 1));
    }
  } else {
    rruleState.value.dayOrder = undefined;
    rruleState.value.daySelection = undefined;

    if (!hasInitialEndingDate) {
      rruleState.value.endingDate = toUnix(addDays(new Date(), 7));
    }
  }
};

const handleCadenceUpdate = (value: PrimitiveModel) => {
  rruleState.value.cadence = value as number;
  rruleState.value.days = getDefaultSelectedDaysForWeek();

  setMonthlyValues();
};

const endOptions: IMultiSelectItem<RepeatRuleEndingTypes>[] = [
  {
    id: "finish_on",
    title: t("templates.rruleinput.finish_on"),
  },
  {
    id: "finish_after",
    title: t("templates.rruleinput.finish_after"),
  },
  {
    id: "no_end_date",
    title: t("templates.no_end_date"),
  },
];

const isValidRepeatRule = computed(() =>
  isValidRRule(rruleState.value.occurrences),
);

let validationTimeout: ReturnType<typeof setTimeout> | null = null;

watch(
  () => JSON.stringify(rruleState.value),
  () => {
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }
    validationTimeout = setTimeout(() => {
      validation.value = validate(VALIDATION_SCHEMA, rruleState.value);
    }, 100);
  },
);

onMounted(() => {
  validation.value = validate(VALIDATION_SCHEMA, rruleState.value);
});
</script>

<template>
  <BigDialog size="xs" @on-close="emit('close')">
    <DialogHeader @close="$emit('close')">
      {{ $t("global.recurrence") }}
    </DialogHeader>

    <DialogContent>
      <FormGroup>
        <SelectInput
          name="repeat_cadence"
          :label="t('global.recurrence')"
          :items="repeatRuleOptions"
          :modelValue="rruleState.cadence"
          :autoDeselect="false"
          @update:modelValue="handleCadenceUpdate"
        />

        <div v-if="rruleState.cadence === 30">
          <HorizontalForm class="tablet:items-end">
            <SelectInput
              name="dayOrder"
              :label="t('templates.nth_date')"
              :items="ORDER_LIST"
              :autoDeselect="false"
              v-model="rruleState.dayOrder"
            />
            <SelectInput
              name="daySelection"
              :items="DAYS_LIST"
              :autoDeselect="false"
              v-model="rruleState.daySelection"
            />
          </HorizontalForm>
        </div>

        <TextInput
          name="occurence"
          :number-only="true"
          :label="t('templates.rruleinput.every')"
          type="text"
          v-model="rruleState.occurence"
          :label-right="typeString"
          :validation="validation"
        />

        <div class="flex justify-between" v-if="rruleState.cadence === 7">
          <Checkbox
            v-for="(day, index) in Object.keys(rruleState.days)"
            :key="index"
            name="daySelection"
            :text="$t(DAYS_IN_WEEK[index]!).slice(0, 3)"
            :is-stacked="isMobile"
            v-model="rruleState.days[index]"
          />
        </div>

        <Line padding="xs" />

        <TagSelectorInput
          name="endingType"
          size="sm"
          :items="endOptions"
          :allowDeselect="false"
          v-model="rruleState.endingType"
        />

        <DatePicker
          v-if="rruleState.endingType === 'finish_on'"
          name="endingDate"
          is-required
          :validation="validation"
          v-model="rruleState.endingDate"
        />

        <TextInput
          v-if="rruleState.endingType === 'finish_after'"
          name="repeatAmount"
          :number-only="true"
          type="text"
          v-model="rruleState.repeatAmount"
          :label-right="
            [1, 7].includes(rruleState.cadence)
              ? $t('templates.amounts')
              : $t('repeats.repetitions')
          "
          :maxlength="4"
          :validation="validation"
        />

        <RRuleTextFetcher class="hidden" v-model="rruleState" />
      </FormGroup>
    </DialogContent>

    <DialogFooterActions>
      <Button
        type="white"
        :text="$t('global.cancel')"
        alignment="center"
        @click="emit('close')"
      />

      <Button
        type="primary"
        :text="$t('global.save')"
        :disabled="!validation?.isValid"
        @click="handleSave"
      />
    </DialogFooterActions>
  </BigDialog>
</template>
